import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Send, Mail, Phone, Clock, MapPin, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-sand/60 bg-white px-4 py-3.5 text-base text-espresso placeholder:text-espresso/40 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 transition-colors";

export default function ContactPage() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const preService = params.get("service") || "";
  const mountedAt = useRef(Date.now());
  const [form, setForm] = useState({
    full_name: "",
    preferred_language: lang,
    email: "",
    phone: "",
    service: preService,
    contact_method: "either",
    preferred_date: "",
    preferred_time: "",
    message: "",
    consent: false,
    company: "",
  });
  const [status, setStatus] = useState("idle");
  const f = t.contact.form;

  const set = (key) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        phone: form.phone || null,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
        elapsed_ms: Date.now() - mountedAt.current,
      });
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <main data-testid="contact-page">
      <Seo title={t.seo.contact.title} desc={t.seo.contact.desc} />
      <PageHero eyebrow={t.contact.eyebrow} title={t.contact.title} sub={t.contact.sub} />

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                {status === "success" ? (
                  <div className="rounded-2xl border border-teal/40 bg-teal/5 p-10 text-center" data-testid="contact-success">
                    <CheckCircle2 className="mx-auto h-12 w-12 text-teal" />
                    <h2 className="mt-5 font-serif text-2xl font-medium text-espresso">{f.successTitle}</h2>
                    <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-espresso/75">{f.successText}</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="rounded-2xl border border-sand/50 bg-cream/50 p-6 sm:p-10" data-testid="contact-form" noValidate={false}>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="cf-name" className="mb-2 block text-sm font-semibold text-espresso">{f.fullName} *</label>
                        <input id="cf-name" data-testid="contact-name-input" required minLength={2} value={form.full_name} onChange={set("full_name")} placeholder={f.fullNamePh} className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="cf-lang" className="mb-2 block text-sm font-semibold text-espresso">{f.language} *</label>
                        <select id="cf-lang" data-testid="contact-language-select" required value={form.preferred_language} onChange={set("preferred_language")} className={inputCls}>
                          <option value="en">{f.langEn}</option>
                          <option value="es">{f.langEs}</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="cf-email" className="mb-2 block text-sm font-semibold text-espresso">{f.email} *</label>
                        <input id="cf-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} placeholder={f.emailPh} className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="cf-phone" className="mb-2 block text-sm font-semibold text-espresso">
                          {f.phone} <span className="font-normal text-espresso/50">({f.phoneOptional})</span>
                        </label>
                        <input id="cf-phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder={f.phonePh} className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="cf-service" className="mb-2 block text-sm font-semibold text-espresso">{f.service} *</label>
                        <select id="cf-service" data-testid="contact-service-select" required value={form.service} onChange={set("service")} className={inputCls}>
                          <option value="" disabled>{f.servicePh}</option>
                          {Object.entries(f.services).map(([slug, label]) => (
                            <option key={slug} value={slug}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="cf-method" className="mb-2 block text-sm font-semibold text-espresso">{f.contactMethod} *</label>
                        <select id="cf-method" data-testid="contact-method-select" required value={form.contact_method} onChange={set("contact_method")} className={inputCls}>
                          <option value="email">{f.methodEmail}</option>
                          <option value="phone">{f.methodPhone}</option>
                          <option value="either">{f.methodEither}</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="cf-date" className="mb-2 block text-sm font-semibold text-espresso">{f.apptDate}</label>
                        <input id="cf-date" type="date" data-testid="contact-date-input" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="cf-time" className="mb-2 block text-sm font-semibold text-espresso">{f.apptTime}</label>
                        <input id="cf-time" type="time" data-testid="contact-time-input" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} />
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="cf-message" className="mb-2 block text-sm font-semibold text-espresso">{f.message} *</label>
                        <textarea id="cf-message" data-testid="contact-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder={f.messagePh} className={inputCls} />
                      </div>
                      <div className="hp-field" aria-hidden="true">
                        <label htmlFor="cf-company">Company</label>
                        <input id="cf-company" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} data-testid="contact-honeypot" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="flex cursor-pointer items-start gap-3" data-testid="contact-consent-label">
                          <input
                            type="checkbox"
                            required
                            checked={form.consent}
                            onChange={set("consent")}
                            data-testid="contact-consent-checkbox"
                            className="mt-1 h-5 w-5 rounded border-sand/60 accent-[#B96E35]"
                          />
                          <span className="text-sm leading-relaxed text-espresso/80">{f.consent}</span>
                        </label>
                      </div>
                    </div>

                    {status === "error" && (
                      <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-800" role="alert" data-testid="contact-error">
                        {f.errorText}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "sending"}
                      data-testid="contact-submit-button"
                      className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 text-base font-semibold text-white transition-colors duration-300 hover:bg-espresso active:scale-95 disabled:opacity-60 sm:w-auto"
                    >
                      <Send className="h-4 w-4" />
                      {status === "sending" ? f.sending : f.submit}
                    </button>

                    <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-espresso/60" data-testid="contact-privacy-notice">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
                      {f.privacyNote}
                    </p>
                  </form>
                )}
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.1} className="space-y-6">
                <div className="rounded-2xl border border-sand/50 bg-espresso p-8 text-cream grain relative overflow-hidden" data-testid="contact-info-card">
                  <h2 className="font-serif text-2xl font-medium">{t.contact.infoTitle}</h2>
                  <ul className="mt-6 space-y-5 text-sm">
                    <li className="flex items-start gap-3">
                      <Mail className="mt-0.5 h-5 w-5 shrink-0 text-sand" />
                      <a href={`mailto:${t.contact.info.email}`} className="text-cream/85 break-all underline-offset-4 hover:text-sand hover:underline transition-colors" data-testid="contact-email-link">{t.contact.info.email}</a>
                    </li>
                    <li className="flex items-start gap-3">
                      <Phone className="mt-0.5 h-5 w-5 shrink-0 text-sand" />
                      <a href="tel:+12135488288" className="text-cream/85 underline-offset-4 hover:text-sand hover:underline transition-colors" data-testid="contact-phone-link">{t.contact.info.phone}</a>
                    </li>
                    <li className="flex items-start gap-3">
                      <Clock className="mt-0.5 h-5 w-5 shrink-0 text-sand" />
                      <span className="text-cream/85">{t.contact.info.hours}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-sand" />
                      <span className="text-cream/85">{t.contact.info.area}</span>
                    </li>
                  </ul>
                  <p className="mt-6 border-t border-sand/25 pt-4 text-xs leading-relaxed text-cream/60" data-testid="contact-response-note">{t.contact.responseNote}</p>
                </div>

                <div className="rounded-2xl border border-teal/40 bg-teal/5 p-6" data-testid="contact-vawa-note">
                  <h3 className="font-serif text-lg font-medium text-espresso">{t.services.vawa.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-espresso/75">{t.contact.vawaNote}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
